import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo.gif';

const DroneCaptureGuidePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#161513] py-12 px-4 sm:px-6">
      {/* Logo Background */}
      <div 
        className="absolute top-0 left-0 right-0 h-[400px] bg-cover bg-center bg-no-repeat opacity-20 z-0 pointer-events-none" 
        style={{ backgroundImage: `url(${logo})` }}
      />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">3D Scanning Guide for Drones</h1>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        </div>

        <div className="bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-[#0CB4E4]/20">
            <p className="text-gray-300 mt-2">A step-by-step guide for capturing high-quality 3D scenes using drones and Gaussian Splatting.</p>
          </div>

          <div className="p-6 md:p-8">
            <div className="prose prose-invert prose-blue max-w-none">
              
              <h2 className="text-xl font-semibold text-white mt-6 mb-3 flex items-center">
                <span className="text-[#0CB4E4] mr-2"></span>
                Preparation
              </h2>

              <ol className="list-decimal pl-5 space-y-4">
                <li className="text-gray-300">
                  <strong className="text-white">Optimal state of the environment</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>
                      <strong className="text-[#0CB4E4]">Lighting:</strong>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Capture during daylight for the best results</li>
                        <li>Execute the capture in the shortest reasonable time to minimize changes in shadows due to the Sun's movement</li>
                        <li>Avoid partially cloudy weather because of the moving cloud shadows</li>
                      </ul>
                    </li>
                    <li>
                      <strong className="text-[#0CB4E4]">Motion:</strong> Choose timing and angles to avoid capturing moving vehicles or other non-stationary objects
                    </li>
                  </ul>
                </li>

                <li className="text-gray-300">
                  <strong className="text-white">Set up your camera</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Choose the widest lens the drone offers to maximize image overlap and scene coverage</li>
                    <li>Make sure to keep all the settings consistent during the shooting</li>
                    <li>Keep dewarping/lens correction either enabled or disabled throughout the shoot</li>
                    <li>Cr3ate currently requires all input images to have the same optical properties; this may be generalized in the future</li>
                  </ul>
                </li>

                <li className="text-gray-300">
                  <strong className="text-white">Research the scan area ahead of time</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Determine the size of the area, points of interest, and any obstacles to make the capture process more efficient</li>
                    <li>The resulting scene will have the highest quality when viewed from a perspective similar to the input images</li>
                    <li>When choosing the flight pattern, take into account which angles viewers will be interested in</li>
                  </ul>
                </li>
              </ol>

              <div className="border-t border-[#0CB4E4]/20 my-8"></div>

              <h2 className="text-xl font-semibold text-white mt-6 mb-3 flex items-center">
                <span className="text-[#0CB4E4] mr-2"></span>
                Capture Process
              </h2>

              <ol className="list-decimal pl-5 space-y-6">
                <li className="text-gray-300">
                  <strong className="text-white">Scan the general area</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-3">
                    <li>
                      Make a series of bird's-eye-view images of the entire area
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Higher altitude will require fewer images to cover the area, but result in less detail</li>
                      </ul>
                    </li>
                    <li>
                      Make a series of 45-degree pictures of the area
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Preferably, this should be done from the north, east, south, and west orientations to capture all sides of the area contents</li>
                        <li>This step is needed to allow the final scene to be viewed from angles other than the bird's-eye view</li>
                      </ul>
                    </li>
                  </ul>
                </li>

                <li className="text-gray-300">
                  <strong className="text-white">Capture points of interest</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>Any object that will need to be inspected up close needs to be captured individually</li>
                    <li>
                      Fly around the object, capturing it from all sides
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Fly as close to the object as it's safe or permitted to maximize the details</li>
                        <li>The whole object does not need to be visible in all images, but image overlap is required</li>
                      </ul>
                    </li>
                    <li>
                      If the object is tall and skinny, it may be circled and captured at multiple altitudes
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>At least one circle around the object needs to be flown below the object height, optimally around the middle of the object height if safe and permissible</li>
                        <li>This results in a better quality of the object in the final scene when looked at from the ground level</li>
                      </ul>
                    </li>
                  </ul>
                </li>

                <li className="text-gray-300">
                  <strong className="text-white">Capture the horizon</strong>
                  <ul className="list-disc pl-5 mt-2 space-y-2">
                    <li>
                      <strong className="text-[#0CB4E4]">If your drone supports panorama capture:</strong>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Take a few 360-degree sphere panoramas near the corners of the recorded area</li>
                        <li>Altitude should be a bit higher than the rest of the photos, such that the horizon is not obstructed in any direction</li>
                        <li><strong>NOTE:</strong> Submit all taken images individually for each panorama, not the stitched one</li>
                      </ul>
                    </li>
                    <li>
                      <strong className="text-[#0CB4E4]">If your drone does not support panorama capture:</strong>
                      <ul className="list-disc pl-5 mt-1 space-y-1">
                        <li>Make a panning 360-degree series of photos capturing the horizon in a few locations near the corners of the recorded area</li>
                        <li>Horizon should be unobstructed in all directions</li>
                        <li>The camera should be kept horizontal, such that half of the image is the sky, and half is below the horizon</li>
                        <li>The ground part of these images must overlap with some images from the previous two steps</li>
                        <li>Altitude for this capture should be the same as for the initial general area scan</li>
                      </ul>
                    </li>
                  </ul>
                </li>
              </ol>

              <div className="mt-8 bg-[#0CB4E4]/10 p-4 rounded-lg border border-[#0CB4E4]/30">
                <h3 className="text-lg font-medium text-white mb-2">Pro Tips</h3>
                <ul className="list-disc pl-5 space-y-2 text-[#0CB4E4]">
                  <li>Use automated flight paths when available to ensure consistent overlap between images</li>
                  <li>Aim for 60-80% overlap between consecutive drone images</li>
                  <li>Consider using the AEB (Auto Exposure Bracketing) feature if available</li>
                  <li>Keep your drone's camera settings consistent throughout the entire capture</li>
                  <li>Monitor battery levels and plan your flight to ensure complete coverage</li>
                </ul>
              </div>

            </div>
          </div>

          <div className="p-6 md:p-8 bg-[#161513]/70 border-t border-[#0CB4E4]/20">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="px-5 py-2.5 bg-[#0C5484] text-white hover:bg-[#0CB4E4]/90 transition-colors shadow-md rounded-lg"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DroneCaptureGuidePage;